/**
 * The project master-data form of PRD §4.4: company (or inline new company), name,
 * BSFZ description, Project ID (Lfd. Nr.), start, and end date.
 *
 * No document drop zone and no AI extraction — both are out of scope (PRD §2.2). New projects
 * inherit `company.bundesland` on the server; this form has no project-level Bundesland field.
 *
 * Inline “Add a new company…” is local state only. The tenant is persisted with the project on
 * submit (`newCompany` on POST /projects), so cancelling this form cannot leave an empty company.
 */

import { useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { DateField } from "@/components/ui/date-field";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { BUNDESLAND_LABELS } from "@/lib/format";
import { useCompanies } from "@/lib/tenancy";
import { BUNDESLAND_CODES, isValidProjectCode, type Bundesland } from "@/shared/schemas/tenancy";

/** Sentinel Select value that reveals the inline new-company mini-form. */
const ADD_COMPANY_VALUE = "__add_company__";
/** Sentinel for a confirmed local draft (not persisted until project submit). */
const DRAFT_COMPANY_VALUE = "__draft_company__";

export interface ProjectFormValues {
  companyId?: string;
  /** Draft tenant — sent as `newCompany` on create; omitted when an existing company is chosen. */
  newCompany?: { name: string; bundesland: Bundesland };
  name: string;
  bsfzProjectDescription: string;
  projectCode: string;
  startDate: string;
  endDate: string;
}

export interface ProjectFormProps {
  initial?: Partial<ProjectFormValues>;
  /** The company is fixed once a project exists — editing never moves it to another tenant. */
  lockCompany?: boolean;
  submitLabel: string;
  busy?: boolean;
  onSubmit: (values: ProjectFormValues) => void;
  onCancel?: () => void;
}

export function ProjectForm({
  initial,
  lockCompany = false,
  submitLabel,
  busy = false,
  onSubmit,
  onCancel,
}: ProjectFormProps) {
  const { t } = useTranslation();
  const companies = useCompanies();

  const [companyId, setCompanyId] = useState(initial?.companyId ?? "");
  const [addingCompany, setAddingCompany] = useState(false);
  const [newCompanyName, setNewCompanyName] = useState("");
  const [newCompanyBundesland, setNewCompanyBundesland] = useState<Bundesland>("BE");
  const [draftCompany, setDraftCompany] = useState<{
    name: string;
    bundesland: Bundesland;
  } | null>(null);

  const [name, setName] = useState(initial?.name ?? "");
  const [bsfzProjectDescription, setBsfzProjectDescription] = useState(
    initial?.bsfzProjectDescription ?? "",
  );
  const [projectCode, setProjectCode] = useState(initial?.projectCode ?? "");
  const [startDate, setStartDate] = useState(initial?.startDate ?? "");
  const [endDate, setEndDate] = useState(initial?.endDate ?? "");

  const companyOptions = companies.data ?? [];

  const projectCodeTrimmed = projectCode.trim();
  const projectCodeOk = projectCodeTrimmed === "" || isValidProjectCode(projectCodeTrimmed);

  function onCompanySelect(value: string) {
    if (value === ADD_COMPANY_VALUE) {
      setAddingCompany(true);
      if (draftCompany) {
        setNewCompanyName(draftCompany.name);
        setNewCompanyBundesland(draftCompany.bundesland);
      }
      return;
    }
    if (value === DRAFT_COMPANY_VALUE) {
      setAddingCompany(false);
      setCompanyId("");
      return;
    }
    if (!value) return;
    setAddingCompany(false);
    setDraftCompany(null);
    setNewCompanyName("");
    setCompanyId(value);
  }

  function confirmDraftCompany() {
    const trimmed = newCompanyName.trim();
    if (!trimmed) return;
    setDraftCompany({ name: trimmed, bundesland: newCompanyBundesland });
    setCompanyId("");
    setAddingCompany(false);
  }

  function submit(event: FormEvent) {
    event.preventDefault();
    if (addingCompany) return;
    const shared = {
      name: name.trim(),
      bsfzProjectDescription: bsfzProjectDescription.trim(),
      projectCode: projectCodeTrimmed,
      startDate,
      endDate,
    };
    if (draftCompany) {
      onSubmit({ newCompany: draftCompany, ...shared });
      return;
    }
    if (!companyId) return;
    onSubmit({ companyId, ...shared });
  }

  const hasCompany = Boolean(companyId) || Boolean(draftCompany);
  const complete = hasCompany && name.trim() && startDate && endDate && projectCodeOk;
  const datesOrdered = endDate >= startDate;
  const selectValue = addingCompany
    ? ADD_COMPANY_VALUE
    : draftCompany
      ? DRAFT_COMPANY_VALUE
      : companyId || undefined;

  return (
    <form onSubmit={submit} className="space-y-10">
      {!lockCompany && (
        <section className="space-y-4" data-testid="project-company-section">
          <div>
            <h2 className="text-2xl">{t("projectForm.companySection")}</h2>
            <p className="mt-1 text-sm text-muted-foreground">
              {t("projectForm.companySectionHint")}
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="project-company">{t("projectForm.company")}</Label>
            <Select value={selectValue} onValueChange={onCompanySelect}>
              <SelectTrigger id="project-company" data-testid="project-company">
                <SelectValue placeholder={t("employees.chooseCompany")} />
              </SelectTrigger>
              <SelectContent>
                {draftCompany && (
                  <SelectItem value={DRAFT_COMPANY_VALUE}>{draftCompany.name}</SelectItem>
                )}
                {companyOptions.map((company) => (
                  <SelectItem key={company.id} value={company.id}>
                    {company.name}
                  </SelectItem>
                ))}
                <SelectItem value={ADD_COMPANY_VALUE}>{t("projectForm.newCompany")}</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {addingCompany && (
            <div
              className="space-y-3 rounded-lg border border-dashed border-border p-4"
              data-testid="project-inline-company"
            >
              <p className="text-sm text-muted-foreground">{t("projectForm.newCompanyHint")}</p>
              <div className="space-y-2">
                <Label htmlFor="inline-company-name">{t("projectForm.companyName")}</Label>
                <Input
                  id="inline-company-name"
                  value={newCompanyName}
                  onChange={(event) => setNewCompanyName(event.target.value)}
                  placeholder={t("companies.namePlaceholder")}
                  data-testid="inline-company-name"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="inline-company-bundesland">{t("companies.formState")}</Label>
                <Select
                  value={newCompanyBundesland}
                  onValueChange={(value) => setNewCompanyBundesland(value as Bundesland)}
                >
                  <SelectTrigger
                    id="inline-company-bundesland"
                    data-testid="inline-company-bundesland"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {BUNDESLAND_CODES.map((code) => (
                      <SelectItem key={code} value={code}>
                        {BUNDESLAND_LABELS[code]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex justify-end gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setAddingCompany(false);
                    if (!draftCompany) setNewCompanyName("");
                  }}
                >
                  {t("common.cancel")}
                </Button>
                <Button
                  type="button"
                  size="sm"
                  disabled={newCompanyName.trim().length === 0}
                  onClick={confirmDraftCompany}
                  data-testid="inline-company-create"
                >
                  {t("projectForm.createCompany")}
                </Button>
              </div>
            </div>
          )}
        </section>
      )}

      <section className="space-y-4" data-testid="project-info-section">
        <div>
          <h2 className="text-2xl">{t("projectForm.infoSection")}</h2>
          <p className="mt-1 text-sm text-muted-foreground">{t("projectForm.infoSectionHint")}</p>
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          <div className="space-y-2 md:col-span-2">
            <Label htmlFor="project-name">{t("projectForm.name")}</Label>
            <Input
              id="project-name"
              value={name}
              onChange={(event) => setName(event.target.value)}
              placeholder={t("projectForm.namePlaceholder")}
              required
              data-testid="project-name"
            />
            <p className="text-xs text-muted-foreground">{t("projectForm.nameHint")}</p>
          </div>

          <div className="space-y-2 md:col-span-2">
            <Label htmlFor="project-bsfz">{t("projectForm.bsfzDescription")}</Label>
            <Textarea
              id="project-bsfz"
              value={bsfzProjectDescription}
              onChange={(event) => setBsfzProjectDescription(event.target.value)}
              placeholder={t("projectForm.bsfzDescriptionPlaceholder")}
              rows={2}
              data-testid="project-bsfz"
            />
            <p className="text-xs text-muted-foreground">{t("projectForm.bsfzDescriptionHint")}</p>
          </div>

          <div className="space-y-2 md:col-span-2">
            <Label htmlFor="project-code">{t("projectForm.projectCode")}</Label>
            <Input
              id="project-code"
              value={projectCode}
              onChange={(event) => setProjectCode(event.target.value)}
              placeholder={t("projectForm.projectCodePlaceholder")}
              data-testid="project-code"
              aria-invalid={!projectCodeOk}
            />
            <p className="text-xs text-muted-foreground">{t("projectForm.projectCodeHint")}</p>
            {!projectCodeOk && (
              <p className="text-xs text-destructive" data-testid="project-code-error">
                {t("projectForm.projectCodeInvalid")}
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="project-start">{t("projectForm.startDate")}</Label>
            <DateField
              id="project-start"
              value={startDate}
              onChange={setStartDate}
              required
              data-testid="project-start"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="project-end">{t("projectForm.endDate")}</Label>
            <DateField
              id="project-end"
              value={endDate}
              min={startDate || undefined}
              onChange={setEndDate}
              required
              data-testid="project-end"
            />
          </div>
        </div>
      </section>

      <div className="flex items-center justify-end gap-2">
        {onCancel && (
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            disabled={busy}
            data-testid="project-cancel"
          >
            {t("common.cancel")}
          </Button>
        )}
        <Button
          type="submit"
          disabled={busy || !complete || !datesOrdered || addingCompany}
          data-testid="project-submit"
        >
          {submitLabel}
        </Button>
      </div>
    </form>
  );
}
